"use client";

import type {
  TextContent as GeneratedTextContentProps,
  PlaceholderValues,
} from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "next-sanity";
import type { ApplyToBeACareGiverButtonVariant } from "~www/components/LeadForm/CareSeeker/CareGiverApplyButtonClient";
import type { PageBuilderOffice } from "~www/types/office";

import { track } from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { RichText } from "@homeinstead/ui/components/RichText";
import { cva } from "class-variance-authority";
import { Suspense, useCallback, useMemo } from "react";
import { ApplyToBeACareGiverButton } from "~www/components/LeadForm/CareSeeker/CareGiverApplyButtonClient";
import { CaregiverDrawerWithSearchParams } from "./CaregiverDrawerWithSearchParams";
import { createMediaRichTextComponents } from "./Media";

const richTextVariants = cva("", {
  variants: {
    contentAlignment: {
      center: "mx-auto text-center",
      left: "mr-auto text-left",
      right: "ml-auto text-right",
    },
  },
});

const richTextContainerVariants = cva("relative", {
  variants: {
    selfAlignment: {
      bottom: "self-end",
      middle: "self-center",
      top: "self-start",
    },
  },
});

const RICH_TEXT_VARIANT_COMPONENTS: Record<
  string,
  Partial<PortableTextComponents>
> = {
  "feature-hero": {
    block: {
      h1: ({ children }) => (
        <h1 className="font-bold font-jakarta text-2xl lg:text-3xl">
          {children}
        </h1>
      ),
    },
  },
};

const CAREGIVER_LEAD_DRAWER_COMPONENTS: Partial<PortableTextComponents> = {
  marks: {
    caregiverLeadDrawer: ({ children, value }) => {
      const variant: ApplyToBeACareGiverButtonVariant =
        value?.variant ?? "plain";
      return (
        <ApplyToBeACareGiverButton variant={variant}>
          {children}
        </ApplyToBeACareGiverButton>
      );
    },
  },
};

function hasCaregiverLeadDrawerMark(blocks: PortableTextBlock[]): boolean {
  return blocks.some(
    (block) =>
      Array.isArray(
        (block as { markDefs?: Array<{ _type: string }> }).markDefs,
      ) &&
      (block as { markDefs: Array<{ _type: string }> }).markDefs.some(
        (def) => def._type === "caregiverLeadDrawer",
      ),
  );
}

type TextContentProps = Omit<GeneratedTextContentProps, "_type"> & {
  placeholders: PlaceholderValues;
  className?: string;
  office?: PageBuilderOffice;
  role?: string;
  richTextVariant?: string;
};

function TextContent({
  text,
  textColor,
  contentAlignment,
  contentVerticalAlignment,
  placeholders,
  className,
  office,
  role,
  richTextVariant,
}: TextContentProps) {
  const variantComponents = richTextVariant
    ? RICH_TEXT_VARIANT_COMPONENTS[richTextVariant]
    : undefined;
  const richTextComponents = useMemo(
    () => ({
      ...variantComponents,
      marks: {
        ...variantComponents?.marks,
        ...CAREGIVER_LEAD_DRAWER_COMPONENTS.marks,
      },
      types: {
        ...variantComponents?.types,
        ...createMediaRichTextComponents().types,
      },
    }),
    [variantComponents],
  );
  const handleLinkClick = useCallback((href: string) => {
    track(EventName.TRACKED_CLICK, {
      element_name: "rich_text_link",
      target_url: href,
    });
  }, []);

  if (!text) {
    return null;
  }

  const blocks = text as PortableTextBlock[];
  const showCaregiverDrawer = hasCaregiverLeadDrawerMark(blocks);

  return (
    <div
      className={cn(
        contentVerticalAlignment
          ? richTextContainerVariants({
              selfAlignment: contentVerticalAlignment,
            })
          : "",
        className,
      )}
      role={role}
    >
      <RichText
        className={cn(
          contentAlignment ? richTextVariants({ contentAlignment }) : "",
        )}
        components={richTextComponents}
        onLinkClick={handleLinkClick}
        placeholders={placeholders}
        textColor={textColor}
        value={blocks}
      />
      {showCaregiverDrawer && (
        <Suspense fallback={null}>
          <CaregiverDrawerWithSearchParams office={office} />
        </Suspense>
      )}
    </div>
  );
}

export { TextContent };
